import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { t } from 'ttag';

import HTTP404 from 'common/components/ErrorRoutes/HTTP404';
import Layout from 'common/components/ErrorRoutes/Layout';
import { isFFEnabled } from 'common/state/flags';
import FeatureFlags from 'common/types/FeatureFlags';

import AudienceResponse from '@domains/AudienceResponse';
import Configuration from '@domains/Configuration';
import { PREMIUM_PRODUCT_KEYS } from '@domains/Premium/constants';
import PremiumRoute from '@domains/Premium/PremiumRoute';

import Details from './Details';

const Privacy = lazy(() => import(/* webpackChunkName: "privacy" */ '@domains/Privacy'));
const People = lazy(() => import(/* webpackChunkName: "people" */ '@domains/People'));
const Sessions = lazy(() => import(/* webpackChunkName: "sessions" */ '@domains/Sessions'));
const Companies = lazy(() => import(/* webpackChunkName: "company" */ '@domains/Company'));
const Documents = lazy(() => import(/* webpackChunkName: "document" */ '@domains/Documents'));
const Videos = lazy(() => import(/* webpackChunkName: "videos" */ '@domains/Videos'));
const Maps = lazy(() => import(/* webpackChunkName: "maps" */ '@domains/Maps'));
const ImageMap = lazy(() => import(/* webpackChunkName: "maps" */ '@domains/Maps/ImageMap'));
const GoogleMap = lazy(() => import(/* webpackChunkName: "maps" */ '@domains/Maps/GoogleMap'));
const Surveys = lazy(() => import(/* webpackChunkName: "surveys" */ '@domains/Surveys'));
const WidgetPages = lazy(
  () => import(/* webpackChunkName: "widgetpages" */ '@domains/WidgetPages'),
);
const WidgetPagesTemplates = lazy(
  () => import(/* webpackChunkName: "widgetpages" */ '@domains/WidgetPages/Templates'),
);
const WidgetPagesPreview = lazy(
  () => import(/* webpackChunkName: "widgetpages" */ '@domains/WidgetPages/Preview'),
);
const WidgetPagesDesigner = lazy(
  () => import(/* webpackChunkName: "widgetpages" */ '@domains/WidgetPages/Designer'),
);
const HomePage = lazy(() => import(/* webpackChunkName: "homepage" */ '@domains/HomePage'));
const Sections = lazy(() => import(/* webpackChunkName: "sections" */ '@domains/Sections'));
const Discussions = lazy(
  () => import(/* webpackChunkName: "discussions" */ '@domains/Discussions'),
);
const Studio = lazy(() => import(/* webpackChunkName: "studio" */ '@domains/Studio'));
const ActivityFeed = lazy(
  () => import(/* webpackChunkName: "activityFeed" */ '@domains/ActivityFeed'),
);
const BannerAd = lazy(() =>
  import(/* webpackChunkName: "bannerAd" */ '@domains/BannerAd').catch(error => {
    if (error.name !== 'ChunkLoadError') {
      throw error;
    }
    return {
      default: () => (
        <Layout
          scoped
          title={t`Ad Blocker Detected`}
          body={t`This page could not be loaded. Please disable ad blockers to access this page.`}
          support={false}
        />
      ),
    };
  }),
);

const Settings = lazy(() => import(/* webpackChunkName: "settings" */ '@domains/Settings'));
const Badges = lazy(() => import(/* webpackChunkName: "badges" */ '@domains/Badges'));
const BadgeDesigner = lazy(
  () => import(/* webpackChunkName: "badges" */ '@domains/Badges/Designer'),
);
const AddBadge = lazy(() => import(/* webpackChunkName: "badges" */ '@domains/Badges/AddBadge'));
const BadgeTemplates = lazy(
  () => import(/* webpackChunkName: "badges" */ '@domains/Badges/Templates'),
);
const Gamification = lazy(
  () => import(/* webpackChunkName: "gamification" */ '@domains/Gamification'),
);
const LiveDisplay = lazy(
  () => import(/* webpackChunkName: "liveDisplay" */ '@domains/LiveDisplay'),
);
const Announcements = lazy(
  () => import(/* webpackChunkName: "announcements" */ '@domains/Announcements'),
);
const Onsite = lazy(() => import(/* webpackChunkName: "onsite" */ '@domains/Onsite'));
const LeadCapture = lazy(
  () => import(/* webpackChunkName: "lead-capture" */ '@domains/LeadCapture'),
);
const MobiAi = lazy(() => import(/* webpackChunkName: "mobi-ai" */ '@domains/MobiAi'));
const Integration = lazy(
  () => import(/* webpackChunkName: "integration" */ '@domains/Integration'),
);
const NativeIntegration = lazy(
  () => import(/* webpackChunkName: "nativeIntegration" */ '@domains/NativeIntegration'),
);
const NativeIntegrationSettings = lazy(
  () =>
    import(
      /* webpackChunkName: "nativeIntegrationSettings" */ '@domains/NativeIntegration/Settings'
    ),
);
const Analytics = lazy(() => import(/* webpackChunkName: "analytics" */ '@domains/Analytics'));
const Audit = lazy(() => import(/* webpackChunkName: "audit" */ '@domains/Audit'));
const CommunicationEmailsOld = lazy(
  () =>
    import(/* webpackChunkName: "Communications-emails-old" */ '@domains/Communications/EmailsOld'),
);
const CommunicationEmailDetailsOld = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails-old" */ '@domains/Communications/EmailsOld/Details'
    ),
);
const SelectEmailTemplateOld = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails-old" */ '@domains/Communications/EmailsOld/Templates/Selection/SelectEmailTemplate'
    ),
);
const SelectTemplateDesignOld = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails-old" */ '@domains/Communications/EmailsOld/Templates/Selection/SelectTemplateDesign'
    ),
);
const EmailEditorOld = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails-old" */ '@domains/Communications/EmailsOld/EmailEditor'
    ),
);
const EmailAnalytics = lazy(
  () =>
    import(/* webpackChunkName: "Communications-analytics" */ '@domains/Communications/Analytics'),
);
const EmailCampaigns = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/Campaigns'
    ),
);
const SystemEmails = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/SystemEmails'
    ),
);
const SystemEmailDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/SystemEmails/Details'
    ),
);
const SystemEmailEditor = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/SystemEmails/Editor'
    ),
);
const EmailSettings = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/Settings'
    ),
);
const CommunicationEmailDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/Details'
    ),
);
const SelectEmailTemplate = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/Templates/Selection/SelectEmailTemplate'
    ),
);
const SelectTemplateDesign = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/Templates/Selection/SelectTemplateDesign'
    ),
);
const EmailEditor = lazy(
  () =>
    import(
      /* webpackChunkName: "Communications-emails" */ '@domains/Communications/Emails/EmailEditor'
    ),
);

const RegistrationDetails = lazy(
  () => import(/* webpackChunkName: "reg-settings" */ '@domains/Registration/Details'),
);
const RegistrationEmails = lazy(
  () => import(/* webpackChunkName: "reg-emails" */ '@domains/Registration/Emails'),
);
const RegistrationOrders = lazy(
  () => import(/* webpackChunkName: "reg-orders" */ '@domains/Registration/Orders'),
);
const RegistrationOrdersOld = lazy(
  () => import(/* webpackChunkName: "reg-orders-old" */ '@domains/Registration/OrdersOld'),
);
const RegistrationOrder = lazy(
  () => import(/* webpackChunkName: "reg-order" */ '@domains/Registration/Orders/Details'),
);
const RegistrationOrderOld = lazy(
  () => import(/* webpackChunkName: "reg-order-old" */ '@domains/Registration/OrdersOld/Details'),
);
const ConfirmationEmailEditor = lazy(
  () =>
    import(
      /* webpackChunkName: "reg-confirmation-email" */ '@domains/Registration/ConfirmationEmailEditor'
    ),
);
const RegistrationPromoCodes = lazy(
  () => import(/* webpackChunkName: "reg-promo-codes" */ '@domains/Registration/PromoCodes'),
);
const RegistrationPromoCode = lazy(
  () => import(/* webpackChunkName: "reg-promo-code" */ '@domains/Registration/PromoCodes/Details'),
);
const RegistrationDiscountCodeDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "reg-discount-code-details" */ '@domains/Registration/Tickets/DiscountCodes/Details'
    ),
);
const RegistrationAccessLinkDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "reg-access-link-details" */ '@domains/Registration/Tickets/AccessLinks/Details'
    ),
);
const RegistrationForm = lazy(
  () => import(/* webpackChunkName: "reg-form" */ '@domains/Registration/Form'),
);
const RegistrationTickets = lazy(
  () => import(/* webpackChunkName: "reg-tickets" */ '@domains/Registration/Tickets'),
);
const RegistrationTicketDetails = lazy(
  () => import(/* webpackChunkName: "reg-ticket" */ '@domains/Registration/Tickets/Details'),
);
const WebsiteBuilder = lazy(
  () => import(/* webpackChunkName: "website-builder" */ '@domains/WebsiteBuilder'),
);
const EmbeddedWidgets = lazy(
  () => import(/* webpackChunkName: "embedded-widgets" */ '@domains/EmbeddedWidgets'),
);
const EmbeddedWidgetDetails = lazy(
  () => import(/* webpackChunkName: "embedded-widgets" */ '@domains/EmbeddedWidgets/Details'),
);
const WebsiteBuilderEditor = lazy(
  () =>
    import(/* webpackChunkName: "website-builder-editor" */ '@domains/WebsiteBuilder/PageEditor'),
);
const SelectTemplate = lazy(
  () => import(/* webpackChunkName: "website-builder" */ '@domains/WebsiteBuilder/Templates'),
);
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ '@domains/Dashboard'));
// we don't really need to lazy load following routes but I'm going to do it
// either way as it's an easy _route_
const Profile = lazy(() => import(/* webpackChunkName: "people" */ '@domains/People/Profile'));
const Session = lazy(() => import(/* webpackChunkName: "sessions" */ '@domains/Sessions/Session'));
const Company = lazy(() => import(/* webpackChunkName: "company" */ '@domains/Company/Profile'));
const Survey = lazy(() => import(/* webpackChunkName: "surveys" */ '@domains/Surveys/Details'));

// Path mode is relative; this Routes block is mounted under
// `/organization/:organizationId/event/:eventId/*` from App.jsx.
const EventRoutes = () => {
  const isGroupRegistrationEnabled = useSelector(state =>
    isFFEnabled(state, FeatureFlags.GroupRegistrationEnabled),
  );
  const isCustomSystemEmailsEnabled = useSelector(state =>
    isFFEnabled(state, FeatureFlags.CustomSystemEmails),
  );
  const isEmbeddedWidgetsEnabled = useSelector(state =>
    isFFEnabled(state, FeatureFlags.EmbeddedWidgetsEnabled),
  );

  return (
    <Routes>
      <Route index element={<Dashboard />} />
      <Route path="details" element={<Details />} />
      <Route path="pages/details" element={<Details />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="people" element={<People />} />
      <Route path="pages/people" element={<People />} />
      <Route path="people/:peopleId" element={<Profile />} />
      <Route path="pages/people/:peopleId" element={<Profile />} />
      <Route path="sessions/details/:sessionId/*" element={<Session />} />
      <Route path="sessions/:sessionId/*" element={<Session />} />
      <Route path="sessions" element={<Sessions />} />
      <Route path="pages/sessions" element={<Sessions />} />
      <Route path="companies" element={<Companies />} />
      <Route path="pages/companies" element={<Companies />} />
      <Route path="companies/:companyId" element={<Company />} />
      <Route path="pages/companies/:companyId" element={<Company />} />
      <Route path="documents" element={<Documents />} />
      <Route path="pages/documents" element={<Documents />} />
      <Route path="videos" element={<Videos />} />
      <Route path="pages/videos" element={<Videos />} />
      <Route path="surveys" element={<Surveys />} />
      <Route path="pages/surveys" element={<Surveys />} />
      <Route path="surveys/:surveyId" element={<Survey />} />
      <Route path="pages/surveys/:surveyId" element={<Survey />} />
      <Route path="ars/settings" element={<AudienceResponse />} />

      {/* designer-pages + event-app share the EventApp premium gate */}
      <Route
        path="designer-pages/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.EventApp}>
            <Routes>
              <Route index element={<WidgetPages />} />
              <Route path=":pageId/templates" element={<WidgetPagesTemplates />} />
              <Route path=":pageId/templates/:templateId" element={<WidgetPagesPreview />} />
              <Route path=":pageId/*" element={<WidgetPagesDesigner />} />
            </Routes>
          </PremiumRoute>
        }
      />
      <Route
        path="event-app/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.EventApp}>
            <Routes>
              <Route path="home-page" element={<HomePage />} />
              <Route path="sections" element={<Sections />} />
              <Route path="discussions" element={<Discussions />} />
              <Route path="activity-feed" element={<ActivityFeed />} />
              <Route path="banner-ads" element={<BannerAd />} />
              <Route path="studio" element={<Studio />} />
              <Route path="settings" element={<Settings />} />
            </Routes>
          </PremiumRoute>
        }
      />

      <Route
        path="badge-designer/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.BadgeDesigner}>
            <Routes>
              <Route index element={<Badges />} />
              <Route path="add-badge" element={<AddBadge />} />
              <Route path="templates" element={<BadgeTemplates />} />
              <Route path=":badgeId" element={<BadgeDesigner />} />
            </Routes>
          </PremiumRoute>
        }
      />

      <Route path="gamification" element={<Gamification />} />
      <Route path="live-display" element={<LiveDisplay />} />
      <Route path="pages/live-display" element={<LiveDisplay />} />

      <Route
        path="onsite"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.OnsiteApp}>
            <Onsite />
          </PremiumRoute>
        }
      />
      <Route
        path="onsite/lead-capture"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.LeadCapture}>
            <LeadCapture />
          </PremiumRoute>
        }
      />

      <Route
        path="communications/emails/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.Communications}>
            <Routes>
              {isCustomSystemEmailsEnabled && (
                <Route index element={<Navigate to="campaigns" replace />} />
              )}
              {isCustomSystemEmailsEnabled && (
                <Route path="campaigns" element={<EmailCampaigns />} />
              )}
              {isCustomSystemEmailsEnabled && <Route path="system" element={<SystemEmails />} />}
              {isCustomSystemEmailsEnabled && (
                <Route path="system/:systemEmailId" element={<SystemEmailDetails />} />
              )}
              {isCustomSystemEmailsEnabled && (
                <Route path="system/:systemEmailId/editor" element={<SystemEmailEditor />} />
              )}
              {isCustomSystemEmailsEnabled && <Route path="settings" element={<EmailSettings />} />}
              {!isCustomSystemEmailsEnabled && <Route index element={<CommunicationEmailsOld />} />}
              <Route
                path=":emailId"
                element={
                  isCustomSystemEmailsEnabled ? (
                    <CommunicationEmailDetails />
                  ) : (
                    <CommunicationEmailDetailsOld />
                  )
                }
              />
              <Route
                path=":emailId/templates"
                element={
                  isCustomSystemEmailsEnabled ? <SelectEmailTemplate /> : <SelectEmailTemplateOld />
                }
              />
              <Route
                path="templates/:emailDesignId/designs"
                element={
                  isCustomSystemEmailsEnabled ? (
                    <SelectTemplateDesign />
                  ) : (
                    <SelectTemplateDesignOld />
                  )
                }
              />
              <Route
                path="templates/:emailDesignId/editor"
                element={isCustomSystemEmailsEnabled ? <EmailEditor /> : <EmailEditorOld />}
              />
              <Route path=":emailId/analytics/*" element={<EmailAnalytics />} />
            </Routes>
          </PremiumRoute>
        }
      />

      <Route path="communications/announcements" element={<Announcements />} />

      <Route path="mobi-ai" element={<MobiAi />} />
      <Route path="integrations" element={<Integration />} />
      <Route path="pages/integrations" element={<Integration />} />
      <Route path="native-integrations" element={<NativeIntegration />} />
      <Route path="native-integration-settings" element={<NativeIntegrationSettings />} />
      <Route path="analytics" element={<Analytics />} />
      <Route path="audit-log" element={<Audit />} />
      <Route path="configuration" element={<Configuration />} />
      <Route path="maps" element={<Maps />} />
      <Route path="maps/:mapId/image/*" element={<ImageMap />} />
      <Route path="maps/:mapId/google/*" element={<GoogleMap />} />

      <Route
        path="registration/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.Registration}>
            <Routes>
              <Route path="email/edit/:emailId/*" element={<ConfirmationEmailEditor />} />
              <Route path="email/*" element={<RegistrationEmails />} />
              <Route
                path="orders"
                element={
                  isGroupRegistrationEnabled ? <RegistrationOrders /> : <RegistrationOrdersOld />
                }
              />
              <Route
                path="orders/:orderId/*"
                element={
                  isGroupRegistrationEnabled ? <RegistrationOrder /> : <RegistrationOrderOld />
                }
              />
              <Route path="details/*" element={<RegistrationDetails />} />
              {/* TODO(EXP-22181): Remove this redirect ~1 month after GroupRegistrationEnabled is fully released */}
              {isGroupRegistrationEnabled && (
                <Route
                  path="promo-codes"
                  element={<Navigate to="../tickets?tab=discount-codes" replace />}
                />
              )}
              {!isGroupRegistrationEnabled && (
                <Route path="promo-codes/:promoCodeId/*" element={<RegistrationPromoCode />} />
              )}
              {!isGroupRegistrationEnabled && (
                <Route path="promo-codes/*" element={<RegistrationPromoCodes />} />
              )}
              <Route path="form/*" element={<RegistrationForm />} />
              <Route path="tickets" element={<RegistrationTickets />} />
              {isGroupRegistrationEnabled && (
                <Route
                  path="tickets/discount-codes/:discountCodeId/*"
                  element={<RegistrationDiscountCodeDetails />}
                />
              )}
              {isGroupRegistrationEnabled && (
                <Route
                  path="tickets/access-links/:accessLinkId/*"
                  element={<RegistrationAccessLinkDetails />}
                />
              )}
              <Route path="tickets/:ticketId/*" element={<RegistrationTicketDetails />} />
            </Routes>
          </PremiumRoute>
        }
      />

      <Route
        path="website-builder/*"
        element={
          <PremiumRoute productKey={PREMIUM_PRODUCT_KEYS.Website}>
            <Routes>
              <Route index element={<WebsiteBuilder />} />
              <Route path="templates" element={<SelectTemplate />} />
              <Route path="details/*" element={<WebsiteBuilder />} />
              <Route path="templates/:pageDesignId/editor" element={<WebsiteBuilderEditor />} />
            </Routes>
          </PremiumRoute>
        }
      />

      {isEmbeddedWidgetsEnabled && (
        <Route
          path="embedded-widgets/*"
          element={
            <Routes>
              <Route index element={<EmbeddedWidgets />} />
              <Route path=":widgetId" element={<EmbeddedWidgetDetails />} />
            </Routes>
          }
        />
      )}

      <Route path="*" element={<HTTP404 scoped ctaText={null} />} />
    </Routes>
  );
};

export default EventRoutes;
